"use client"

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EFeatures } from "@/src/constants";
import { useUser } from "@/src/contexts";
import { User } from "@/src/models";
import {
  ArrowRightLeft,
  Bell,
  FileText,
  FolderOpen,
  LayoutDashboard,
  LogOut,
  Menu,
  ReceiptText,
  Search,
  Settings,
  User as UserIcon,
  X
} from 'lucide-react';
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { Suspense } from 'react';
import { useState } from 'react';

import LogoutForm from "../../forms/LogoutForm";
import { FreeTierBanner } from "../../shared/core/FreeTierBanner";
import { MissingSubscriptionBanner } from "../../shared/core/MissingSubscriptionBanner";
import { Avatar, AvatarFallback, AvatarImage, Skeleton } from "../../ui";

interface DashboardNavigationWrapperProps {
  children: React.ReactNode;
  stripeProduct?: { name: string } | null;
  billingPortalURL?: string | null;
}

export const DashboardNavigationWrapper: React.FC<DashboardNavigationWrapperProps> = ({ children, stripeProduct, billingPortalURL }) => {
  const path = usePathname();
  const user: User | null = useUser();
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Account', href: '/dashboard/account', icon: UserIcon },
  ];

  // { name: 'CV Management', href: '/dashboard/cvs', icon: FolderOpen },

  const isActive = (checkPath: string) => path === checkPath;
  const showFreeBanner = !user?.hasAccess(EFeatures.VIEW_HIRING_TEAM_EMAILS);
  const stripePlan = stripeProduct?.name || null;
  return (
  <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden" 
          onClick={() => setIsMobileMenuOpen(false)} 
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 bg-white shadow-lg transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0 lg:z-auto
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="sticky top-0">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-2 border-b lg:justify-center">
            <Link className="mr-1 flex items-center logo-link" href="/dashboard">
                <div className="logo-wrapper relative">
                    <div className="absolute m-auto top-0 bottom-0">
                        <Image src="/logo.png" decoding="async" alt="logo" width="820" height="820" sizes="22px" className="logo" />
                    </div>
                </div>
                <div className="logo-text-wrapper">
                    <p data-testid="brand-name">Job Compass</p>
                </div>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  href={item.href}
                  className={`
                    flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors
                    ${isActive(item.href)
                      ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }
                  `}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Bottom section */}
          <div className="border-t px-4 py-4 space-y-1">
            <Link className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors
                ${isActive('/subscribe') ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
            href={"/subscribe"}
            onClick={() => setIsMobileMenuOpen(false)}
              >
                <ArrowRightLeft className="mr-3 h-4 w-4" />
                Change plan
            </Link>
            { billingPortalURL && 
            <Link className="flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors" href={billingPortalURL}>
              <ReceiptText className="mr-3 h-4 w-4" />
                    Billing
            </Link> }
            <LogoutForm />
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="sticky top-0 z-40 bg-white border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-16 items-center justify-between border-b px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            
            {/* Show logo on mobile when sidebar is closed */}
            <Link href="/dashboard" className="text-xl font-bold text-blue-700 lg:hidden">
              <div className=" flex items-center logo-link">
                <div className="logo-wrapper relative">
                    <div className="absolute m-auto top-0 bottom-0">
                        <Image src="/logo.png" decoding="async" alt="logo" width="820" height="820" sizes="22px" className="logo" />
                    </div>
                </div>
                <div className="logo-text-wrapper">
                    <p data-testid="brand-name">Job Compass</p>
                </div>
              </div>
            </Link>
            </div>

            <div className="flex items-center gap-4">
              <Suspense fallback={<Badge variant="outline" className="mr-2">
                  <Skeleton className="w-[50px] h-[20px] rounded-full" /></Badge>}>
                  { stripePlan && <Badge variant="outline" className="mr-2">{stripePlan?.toUpperCase()}</Badge> }
              </Suspense>
              
              { user && <div className="flex items-center gap-3">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-slate-900">{user.name}</p>
                  <p className="text-xs text-slate-500">{user.email}</p>
                </div>
                <Avatar className="h-8 w-8 border">
                    <AvatarImage src={user.rapid_linkedin_profile_data_response?.profilePicture} alt={user.name} />
                    <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
                </Avatar>
              </div> }
            </div>
          </div>
          { showFreeBanner && stripePlan && <FreeTierBanner /> } 
          { !stripePlan && <MissingSubscriptionBanner />}  
        </div>
     

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};
